import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import ClientLogsService from "@src/services/client-logs.service";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { Link } from "@uhg-abyss/web/ui/Link";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Text } from "@uhg-abyss/web/ui/Text";
import { useEffect, useRef, useState } from "react";

const BioContainer = styled("div", {
  textAlign: "left",
});

const BioContentWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const BioParagraph = styled("div", {
  whiteSpace: "pre-line",
  variants: {
    isMobile: {
      true: { fontSize: "16px" },
      false: { fontSize: "18px" },
    },
    isClipped: {
      true: {
        display: "-webkit-box",
        WebkitLineClamp: 14,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      },
    },
  },
});

export const ProviderBio = ({
  providerBio,
  showHeading = true,
}: {
  providerBio: string;
  showHeading?: boolean;
}) => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const bioRef = useRef<HTMLDivElement>(null);
  const [lineCount, setLineCount] = useState<number>(0);
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const maxLines = 14;
  const { data } = useApplicationContext();

  useEffect(() => {
    if (bioRef.current && providerBio) {
      const timer = setTimeout(() => {
        const element = bioRef.current;
        if (element) {
          const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
          const height = element.offsetHeight;

          if (!isNaN(lineHeight) && lineHeight > 0 && height > 0) {
            const lines = Math.round(height / lineHeight);
            setLineCount(lines);
          }
        }
      }, 0);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [providerBio, isMobile, isTablet]);

  return (
    <BioContainer data-testid="test-id-provider-bio-component">
      {showHeading && (
        <Heading
          size="sm"
          css={{
            "abyss-heading-root": {
              marginBottom: "16px",
              lineHeight: isMobile ? "24px" : "28px",
            },
          }}
        >
          About me and my practice
        </Heading>
      )}
      <BioContentWrapper>
        <BioParagraph
          data-testid="test-id-provider-bio-paragraph"
          ref={bioRef}
          isMobile={isMobile}
          isClipped={lineCount >= maxLines}
        >
          {providerBio}
        </BioParagraph>
        {lineCount > maxLines && (
          <div>
            {" "}
            <Link
              id="obc-provider-bio-read-more-link"
              data-testid="test-id-provider-bio-read-more-link"
              data-obc-label="Provider Bio Read More Link"
              onClick={() => {
                setIsBioModalOpen(true);
              }}
              css={{
                "abyss-link-root": {
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                },
              }}
            >
              Read more
            </Link>
          </div>
        )}
      </BioContentWrapper>
      <ModalDialog
        data-testid="test-id-provider-bio-modal-dialog"
        data-id="obc-provider-bio-modal-dialog"
        data-obc-label="Provider Bio Modal Dialog"
        header="About me and my practice"
        css={{
          "abyss-modal-base-header-container": {
            padding: "16px 30px",
          },
          "abyss-modal-base-body": {
            padding: "6px 30px 16px",
          },
        }}
        isOpen={isBioModalOpen}
        onClose={() => {
          ClientLogsService.logClick(
            data.userUuid || "unknown",
            "ProviderBio",
            "obc-provider-bio-modal-close-btn",
            "Provider Bio Modal Close Button",
          );
          setIsBioModalOpen(false);
        }}
      >
        <Text css={{ whiteSpace: "pre-line" }}>{providerBio}</Text>
      </ModalDialog>
    </BioContainer>
  );
};
